/**
 * Numéro 3 du programme pédagogique ETM — Le conducteur.
 *
 * Thème ETM 'L' (Le conducteur : alcool, stupéfiants, fatigue, distraction).
 * Le thème le plus mortel à moto. Approche storytelling sur le cerveau du motard.
 */

import { Brain, AlertTriangle, Lightbulb, Quote, Users } from 'lucide-react';

export const conducteurEtmIssue = {
  // META
  slug: 'conducteur',
  themeCode: 'L',
  issueNumber: 3,
  totalIssues: 9,
  category: 'Le conducteur',
  shortTitle: 'Le conducteur',
  title: 'Ton cerveau, ton meilleur EPI',
  kicker: 'Numéro 3 du programme pédagogique ETM',
  subtitle:
    "Alcool, stupéfiants, fatigue, distraction, médicaments — tout ce qui se passe dans ta tête quand tu prends le guidon. Le thème ETM qui sauve le plus de vies… si tu le maîtrises.",
  heroImage: '/img/magazines/conducteur.jpg',
  heroImageAlt: 'Motard avec casque blanc en pleine conduite sur la route — illustration de la vigilance du conducteur',
  readTimeMin: 14,

  seoDescription:
    "Comprendre l'état du conducteur moto avant l'ETM 2026 : seuils alcool, stupéfiants, fatigue, distraction, médicaments. Lecture pédagogique + QCM final auto-évalué.",
  seoKeywords:
    "alcool moto, stupéfiants moto, fatigue moto, distraction moto, médicaments conduite, ETM état conducteur",

  edito: {
    title: 'Pourquoi ce numéro pèse plus que tout l\'équipement réuni',
    body: "Statistique brutale : <strong>40 % des motards tués</strong> avaient un taux d'alcool supérieur à 0,5 g/L dans le sang. Aucun équipement ne te sauve quand ton cerveau ne fonctionne plus. Ce numéro de 14 minutes te donne tout ce que l'ETM teste sur ton état mental : seuils légaux, effets réels, médicaments piégeux, fatigue, distraction. À la fin, le QCM de 10 questions te dira si tu as compris ce qui te tuera ou te sauvera la nuit prochaine.",
    signedBy: 'La rédaction Code 2 Roues',
  },

  sections: [
    // === CHAPITRE 1 — Alcool ===
    {
      type: 'lesson',
      icon: Brain,
      partLabel: 'Chapitre 1',
      title: "L'alcool : ce que dit la loi (et ce qu'elle ne dit pas)",
      paragraphs: [
        "Le Code de la route française pose <strong>deux seuils</strong> distincts qu'il faut connaître par cœur pour l'ETM :",
        "<strong>0,5 g/L de sang (ou 0,25 mg/L d'air expiré)</strong> : seuil de la contravention. Au-dessus, c'est <strong>135 € + 6 points retirés</strong> + immobilisation possible du véhicule. Tu peux conduire jusqu'à 0,49 g/L sans sanction.",
        "<strong>0,8 g/L de sang (ou 0,4 mg/L air)</strong> : seuil du délit. Au-dessus, c'est le tribunal correctionnel : <strong>jusqu'à 4 500 € d'amende, 2 ans de prison, suspension de permis 3 ans, voire annulation</strong>. C'est aussi une inscription au casier judiciaire.",
        "<strong>Permis probatoire (3 premières années) : 0,2 g/L</strong>. Pratiquement un verre = infraction. Cette tolérance zéro vaut aussi pour les conducteurs en récidive et les conducteurs de transports en commun.",
        "Calcul approximatif : <strong>1 verre standard (10g d'alcool) = +0,15 à 0,25 g/L</strong> selon ton poids et ton sexe. Un homme de 70 kg qui boit 2 bières atteint déjà 0,4 g/L. Une femme de 55 kg : 0,6 g/L.",
      ],
      keyPoints: [
        '0,5 g/L = contravention (135 € + 6 points)',
        '0,8 g/L = délit (tribunal correctionnel, casier)',
        'Permis probatoire & récidive : 0,2 g/L (quasi-zéro)',
        "L'éthylotest est obligatoire dans tout véhicule depuis 2012",
      ],
    },

    {
      type: 'didyouknow',
      icon: Lightbulb,
      title: 'Le saviez-vous ?',
      body: "Le corps humain élimine l'alcool à un rythme <strong>fixe</strong> : environ <strong>0,1 à 0,15 g/L par heure</strong>. Aucun café, douche froide, ou exercice ne peut accélérer ce processus. Si tu as 0,8 g/L à 1h du matin, tu seras encore au-dessus de 0,5 g/L vers 4h-5h du matin. Le « petit déj qui fait dégriser » est un mythe : si tu as bu beaucoup la veille, tu peux être encore positif à 10h le lendemain.",
    },

    // === CHAPITRE 2 — Stupéfiants ===
    {
      type: 'lesson',
      icon: Brain,
      partLabel: 'Chapitre 2',
      title: "Les stupéfiants : tolérance ZÉRO, sans exception",
      paragraphs: [
        "Contrairement à l'alcool, il n'y a <strong>aucun seuil légal</strong> pour les stupéfiants : un test positif suffit à constituer le délit. Cannabis, cocaïne, ecstasy, amphétamines, opiacés — la moindre trace dans le sang ou la salive et tu es en infraction.",
        "Sanction : <strong>jusqu'à 4 500 € d'amende, 2 ans de prison, retrait de 6 points, suspension de permis jusqu'à 3 ans</strong>. Si tu cumules avec l'alcool (cas fréquent), les peines doublent.",
        "Le cannabis reste <strong>détectable dans la salive 8 à 12 heures après consommation</strong>, dans le sang jusqu'à 5 jours pour un usage régulier. Les contrôles salivaires se généralisent depuis 2016 sur le bord de la route — 1 minute suffit pour un test positif.",
        "Effets sur la conduite : altération du temps de réaction (+50 % minimum), distorsion de la perception des distances et des vitesses, somnolence, prise de risque accrue. À moto, la moindre micro-distraction = chute potentielle.",
      ],
      keyPoints: [
        'Stupéfiants = TOLÉRANCE ZÉRO (pas de seuil)',
        'Cannabis détectable dans la salive jusqu\'à 12h',
        'Cumul alcool + stupéfiants = peines doublées',
        '+50 % minimum de temps de réaction',
      ],
    },

    {
      type: 'trap',
      icon: AlertTriangle,
      title: 'Le piège à éviter',
      body: "« Mes médicaments sont prescrits par mon médecin, donc je peux conduire. » <strong>FAUX.</strong> De nombreux médicaments en vente libre ou sur ordonnance affectent la conduite. Les pictogrammes <strong>triangulaires jaunes/oranges/rouges</strong> sur les boîtes (depuis 2017) indiquent le niveau de risque : niveau 3 (rouge) = <strong>conduite déconseillée formellement</strong>. Anxiolytiques (Lexomil, Xanax), antidépresseurs, antihistaminiques (Polaramine, Reactine), antitussifs codeinés, somnifères : tous peuvent te valoir un retrait de permis s'ils altèrent ton comportement, même légalement prescrits.",
    },

    // === CHAPITRE 3 — Fatigue ===
    {
      type: 'lesson',
      icon: Brain,
      partLabel: 'Chapitre 3',
      title: "La fatigue : l'autre 'alcool' qui ne se voit pas",
      paragraphs: [
        "<strong>17 heures sans sommeil = effet équivalent à 0,5 g/L d'alcool dans le sang.</strong> Cette étude australienne (1997, confirmée maintes fois) a révolutionné la perception du risque fatigue. Pourtant, aucune sanction directe : c'est à toi de la détecter.",
        "Les signaux d'alarme à reconnaître absolument à moto : bâillements répétés, paupières lourdes, regard fixe sur la bande blanche centrale, micro-coups de volant, oubli des kilomètres parcourus. Le plus dangereux : le <strong>« micro-sommeil »</strong>, où tu fermes les yeux 2-5 secondes sans t'en rendre compte. À 90 km/h, c'est 100 mètres yeux fermés.",
        "À moto, la fatigue est <strong>exacerbée</strong> : la concentration musculaire constante (rétention, freinage, équilibre), le vent, les vibrations, la chaleur du moteur. Un trajet de 2 h de moto vaut largement 3 h de voiture en charge mentale.",
        "Règle d'or : pause de <strong>15 minutes minimum toutes les 2 heures</strong>. Hydrate-toi. Si possible, marche un peu. Le café aide, mais ne remplace pas le sommeil — il masque seulement les symptômes pendant 30-40 minutes.",
      ],
      keyPoints: [
        '17 h sans sommeil = 0,5 g/L d\'alcool',
        'À moto : pause 15 min toutes les 2 h obligatoire',
        'Micro-sommeil = 100 m yeux fermés à 90 km/h',
        'Le café ne remplace JAMAIS le sommeil',
      ],
    },

    // === CHAPITRE 4 — Distraction ===
    {
      type: 'lesson',
      icon: Brain,
      partLabel: 'Chapitre 4',
      title: 'La distraction : ton téléphone, ton intercom, ton GPS',
      paragraphs: [
        "Tenir un téléphone en main au guidon est <strong>strictement interdit</strong> (article R412-6-1) : 135 € + 3 points retirés. Mais ce n'est pas tout : depuis 2020, conduire avec un téléphone à l'oreille même via oreillette filaire ou Bluetooth est <strong>interdit</strong>. Seuls les systèmes <strong>intégrés au casque ou kit mains-libres intégré moto</strong> restent autorisés.",
        "<strong>Lire un SMS au guidon = multiplier par 23 le risque d'accident.</strong> C'est l'équivalent comportemental d'un taux d'alcool de 1,0 g/L. À 50 km/h, 2 secondes les yeux sur l'écran = 28 mètres parcourus à l'aveugle.",
        "Le GPS au guidon est toléré, mais doit être <strong>fixé solidement</strong> et configuré avant le départ. Manipuler l'écran en roulant = même infraction que le téléphone.",
        "L'intercom moto est légal et même <strong>recommandé</strong> entre conducteur et passager (communication = anticipation). Mais limite les conversations qui demandent une charge cognitive (négociation pro, dispute), elles dégradent ton attention au même titre qu'un téléphone tenu en main.",
      ],
      keyPoints: [
        'Téléphone tenu en main au guidon = 135 € + 3 points',
        'Lire un SMS au guidon = ×23 le risque d\'accident',
        "Oreillette filaire ou Bluetooth dans l'oreille = interdit",
        "Intercom intégré casque = autorisé",
      ],
    },

    {
      type: 'tip',
      icon: Quote,
      title: 'Le conseil du moniteur ECSR',
      body: "« La règle que je donne à tous mes élèves : <strong>si tu as bu, tu prends un Uber, point.</strong> Ne calcule pas, ne discute pas, ne te dis pas \"juste 5 minutes\". Le calcul mental après 2 verres est précisément la première fonction altérée par l'alcool. Et n'oubliez pas : <strong>l'alcoolémie est calculée au moment du contrôle, pas au moment où tu as bu.</strong> Donc si tu pars à 23h avec 0,4 g/L et que tu es contrôlé à 23h15, tu es bon. Si tu repars à 4h du matin, tu seras peut-être encore à 0,3 g/L (probatoire = infraction). »",
    },

    {
      type: 'story',
      icon: Users,
      title: 'Le témoignage : « Un seul verre m\'a coûté mon permis »',
      body: "« Je suis sortie boire UN apéro avec une collègue après le boulot, j'avais 1 mois de permis. Un verre de rosé, vraiment, je te jure. Sur le retour, contrôle. 0,28 g/L. J'étais à 0,08 g/L au-dessus du seuil probatoire de 0,2. <strong>Permis suspendu 3 mois, 135 € d'amende, 6 points retirés sur 6 = invalidation, repasser le code et la conduite</strong>. Pour un verre. Je n'avais même pas le sentiment d'avoir bu. Le permis probatoire, ça ne pardonne RIEN. » <em>— Léa, 19 ans</em>",
    },
  ],

  keyStat: {
    kicker: 'Le chiffre à retenir',
    big: '40 %',
    middle: 'des motards tués',
    label: 'avaient un taux d\'alcool supérieur à 0,5 g/L dans le sang',
    source: 'ONISR — Bilan accidentologie 2024',
    accent: '#cf2031',
  },

  glossary: [
    { term: '0,2 g/L', def: 'Seuil d\'alcoolémie pour le permis probatoire (3 premières années) ET les conducteurs en récidive. Quasiment zéro tolérance.' },
    { term: '0,5 g/L', def: 'Seuil de la contravention pour permis confirmé. Au-dessus : 135 € + 6 points retirés.' },
    { term: '0,8 g/L', def: 'Seuil du délit. Au-dessus : tribunal correctionnel, amende jusqu\'à 4 500 €, prison possible.' },
    { term: 'Éthylotest', def: 'Dispositif de mesure du taux d\'alcool dans l\'air expiré. Obligatoire dans tout véhicule depuis 2012 (un usage). Conversion : 0,25 mg/L air = 0,5 g/L sang.' },
    { term: 'Stupéfiants', def: 'Toutes substances classées comme tels par la loi : cannabis, cocaïne, MDMA, héroïne, amphétamines, certains médicaments détournés. Test salivaire positif = infraction.' },
    { term: 'Pictogramme médicament', def: 'Triangle jaune (niveau 1), orange (niveau 2) ou rouge (niveau 3) sur les boîtes de médicaments. Niveau 3 = conduite formellement déconseillée.' },
    { term: 'Micro-sommeil', def: 'Endormissement involontaire de 2 à 5 secondes, pendant lequel la personne ne se rend pas compte d\'avoir fermé les yeux. À 90 km/h = 100 m à l\'aveugle.' },
    { term: 'Temps de réaction', def: 'Délai entre la perception d\'un danger et le début de l\'action (freinage, évitement). Normal : 1 seconde. Sous alcool/fatigue : 1,5 à 2 secondes.' },
    { term: 'Kit mains-libres intégré', def: 'Système de communication moto/casque (Bluetooth) installé DANS le casque, sans oreillette amovible. Seul système autorisé pour les appels en roulant.' },
    { term: 'Casier judiciaire', def: 'Registre national des condamnations pénales. Un délit routier (alcool 0,8+, stupéfiants, refus d\'obtempérer) y est inscrit. Peut empêcher l\'accès à certains emplois.' },
  ],

  quiz: {
    title: 'Teste ta compréhension',
    description:
      "10 questions sur l'état du conducteur tirées de la banque officielle ETM. Tu dois en réussir <strong>au moins 8 sur 10</strong> pour valider ta maîtrise du thème. C'est le sujet le plus piégeux de l'ETM : prends ton temps.",
    themeCode: 'L',
    questionsCount: 10,
    passingScore: 8,
  },

  nextIssue: {
    slug: 'premiers-secours',
    number: 9,
    title: 'Tu peux sauver une vie en 3 gestes',
    short: 'Les premiers secours',
  },
};

export default conducteurEtmIssue;
